import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { toastError } from '@futrli/ui-components';
import {
  AmplifyException,
  APIClients,
  ApiClientType,
  auth0Authenticator,
  IntegrationsAPIClient,
  Logger,
  validateUserEmail
} from '@futrli/shared-services';

import { useFederatedSignIn } from '../../federated-sign-in/use-federated-sign-in';
import { trackEvent } from '../../../../predict/src/core/tracker';
import { TRACKING_EVENTS } from '../../../../predict/src/core/constants';
import { resetError } from '../../core/redux-utils/actions';
import { SIGN_UP } from '../constants';
import { GET_CURRENT_AUTHENTICATED_USER } from '../../auth/constants';
import { AuthError } from '../../../../auth/src/core/types';
import { SageRegisterForm, SageRegisterFormData } from './sage-register-form';
import { clearLocalStorageWithExceptions } from '@futrli/shared-services/string-utils/utils';

/**
 * Register user page.
 */
export const SageRegister: React.FC = () => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [starterLoading, setStarterLoading] = useState(false);
  const [sageIdError, setSageIdError] = useState<AuthError>();

  const dispatch = useDispatch();

  useEffect(() => {
    // Initially set it to AWS one so unauthenticated requests can be processed
    APIClients.setApiClient(ApiClientType.Aws);
    IntegrationsAPIClient.setApiClient(ApiClientType.Aws);

    clearLocalStorageWithExceptions();
    sessionStorage.clear();
    return () => {
      dispatch(resetError(SIGN_UP));
      dispatch(resetError(GET_CURRENT_AUTHENTICATED_USER));
    };
  }, [dispatch]);

  const { isFederatedSignInLoading, federatedSignInError, handleFederatedSignIn } = useFederatedSignIn();

  const handleRedirect = useCallback((email: string) => {
    setIsRedirecting(true);
    trackEvent(TRACKING_EVENTS.STARTED_TRIAL_DIRECT);
    APIClients.setApiClient(ApiClientType.Auth0);
    IntegrationsAPIClient.setApiClient(ApiClientType.Auth0);
    auth0Authenticator.signIn(email);
  }, []);

  const handleNewSubmit = async (values: SageRegisterFormData) => {
    const { username } = values;
    if (!username) {
      return;
    }

    try {
      setStarterLoading(true);
      const { redirect, existing } = await validateUserEmail(username);
      if (existing) {
        setSageIdError({ code: AmplifyException.UsernameExists, message: '', name: 'sageIdError' });
      } else if (redirect) {
        handleRedirect(username);
      }
    } catch (e) {
      toastError();
      Logger.error(e as string);
    } finally {
      setStarterLoading(false);
    }
  };

  const isLoading = isFederatedSignInLoading || starterLoading || isRedirecting;

  return (
    <SageRegisterForm
      isLoading={isLoading}
      federatedSignInError={federatedSignInError}
      sageIdError={sageIdError}
      onSubmit={handleNewSubmit}
      onFederatedSignUp={handleFederatedSignIn}
    />
  );
};
